import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Divider,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import { usePostBulkSalaryMutation } from "../../../redux/api/salary";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export default function AgentsEditor({ agents, selectedDate }) {
  const [rows, setRows] = useState(agents || []);
  const currentAgency = useSelector(selectCurrentagency);
  const [postBulkSalary, { isLoading }] = usePostBulkSalaryMutation();

  const calculateNetBalance = (row) => {
    const base = row.BaseSalary ?? 0;
    const adv = row.AdvanceSalary ?? 0;
    const ded = row.Deductions ?? 0;
    const ot = row.Overtime ?? 0;
    const bonus = row.Bonus ?? 0;
    const commission = row.Commission ?? 0;
    const allowances = row.Allowances ?? 0;
    return base + bonus + commission + allowances + ot - (ded + adv);
  };

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    if (
      [
        "BaseSalary",
        "AdvanceSalary",
        "Deductions",
        "Overtime",
        "Bonus",
        "Commission",
        "Allowances",
      ].includes(field)
    ) {
      updated[index].NetBalance = calculateNetBalance(updated[index]);
    }
    setRows(updated);
  };

  const handleSave = async () => {
    try {
      const monthYear = selectedDate
        ? dayjs(selectedDate).startOf("month").format("YYYY-MM-DDTHH:mm:ss")
        : dayjs().startOf("month").format("YYYY-MM-DDTHH:mm:ss");

      const payload = rows.map((row) => ({
        AgentId: row.agentid,
        BaseSalary: row.BaseSalary ?? 0,
        NetSalary: calculateNetBalance(row),
        DueAmount: row.DueAmount ?? 0,
        Bonus: row.Bonus ?? 0,
        Commission: row.Commission ?? 0,
        Allowances: row.Allowances ?? 0,
        Deductions: row.Deductions ?? 0,
        Overtime: row.Overtime ?? 0,
        AdvanceDeductions: row.AdvanceSalary ?? 0,
        MonthYear: monthYear,
        AgencyId: currentAgency.agencyid,
        PaymentStatus: row.PaymentStatus || "Paid",
        PaidDate: row.PaidDate
          ? dayjs(row.PaidDate).format("YYYY-MM-DDTHH:mm:ss")
          : dayjs().format("YYYY-MM-DDTHH:mm:ss"),
        UserId: 1,
        Balance: row.Balance ?? 0,
      }));

      await postBulkSalary(payload).unwrap();
    } catch (err) {
      console.error("Failed to save bulk salary ❌", err);
    }
  };

  useEffect(() => {
    if (agents) {
      const initialized = agents.map((a) => ({
        ...a,
        NetBalance: calculateNetBalance(a),
        PaidDate: a.PaidDate
          ? dayjs(a.PaidDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
      }));
      setRows(initialized);
    }
  }, [agents]);

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        fontWeight="bold"
        color="primary.main"
      >
        Agents Salary & Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {rows.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No agents found.
        </Typography>
      ) : (
        rows.map((row, index) => (
          <Card
            key={row.agentid}
            sx={{
              mb: 3,
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
              transition: "0.25s",
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {row.firstName} {row.lastName}
                </Typography>
                <Typography variant="caption" color="primary">
                  {row.roleName}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block" }}
              >
                Agent ID: {row.agentid}
              </Typography>

              <Grid container spacing={2}>
                {[
                  ["Base Salary", "BaseSalary"],
                  ["Advance Salary", "AdvanceSalary"],
                  ["Deductions", "Deductions"],
                  ["Overtime", "Overtime"],
                  ["Bonus", "Bonus"],
                  ["Commission", "Commission"],
                  ["Allowances", "Allowances"],
                ].map(([label, key]) => (
                  <Grid item xs={12} sm={6} md={1.7} key={key}>
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      label={label}
                      value={row[key] ?? ""}
                      onChange={(e) =>
                        handleChange(
                          index,
                          key,
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </Grid>
                ))}

                <Grid item xs={12} sm={6} md={1.7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Net Balance"
                    value={row.NetBalance ?? 0}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={1.7}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Payment Status"
                    value={row.PaymentStatus || "Paid"}
                    onChange={(e) =>
                      handleChange(index, "PaymentStatus", e.target.value)
                    }
                  >
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Unpaid">Unpaid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={1.7}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Paid Date"
                      format="YYYY-MM-DD"
                      value={dayjs(row.PaidDate || dayjs())}
                      onChange={(newValue) =>
                        handleChange(
                          index,
                          "PaidDate",
                          dayjs(newValue).format("YYYY-MM-DD")
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
      )}

      {rows.length > 0 && (
        <Box sx={{ textAlign: "right", mt: 3 }}>
          <Button
            variant="contained"
            color="success"
            onClick={handleSave}
            disabled={isLoading}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: "0 3px 10px rgba(76,175,80,0.3)",
            }}
          >
            {isLoading ? "Saving..." : "Save All"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
